"use client"

import { DefaultImage } from "@/lib/external-components"
import { getLocalizedHref } from "@/lib/locale-utils"
import { formatDateSimple, generateImagePath } from "@/lib/utils"
import { SubFeaturePostsProps } from "@/types/types"
import Image from "next/image"
import Link from "next/link"
import { twMerge } from "tailwind-merge"

const SubFeaturePosts = ({
  posts,
  locale = "vi",
  isVideo = false,
  youtubeUrlMap = {},
  onVideoClick,
}: SubFeaturePostsProps) => {
  if (posts.length === 0) return null

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 w-full">
      {posts.map(post => {
        const blogUrl = getLocalizedHref(post.attributes.locale, `/${post.attributes.slug}`)
        const youtubeUrl = isVideo ? youtubeUrlMap[post.id] : undefined
        const imgAttr = post.attributes?.cover?.data?.attributes
        const imageUrl = imgAttr?.url
          ? generateImagePath(imgAttr.url, "small", imgAttr.formats)
          : DefaultImage
        const postTitle = post.attributes?.name || post.attributes?.title || ""
        const publishedAt = post.attributes?.publishedAt || post.attributes?.updatedAt || ""

        const handleClick = (e: React.MouseEvent) => {
          if (isVideo && youtubeUrl && onVideoClick) {
            e.preventDefault()
            onVideoClick(youtubeUrl)
          }
        }

        return (
          <Link
            key={post.id}
            href={isVideo && youtubeUrl ? "#" : blogUrl}
            onClick={handleClick}
            className={twMerge("group flex flex-col gap-1", isVideo && youtubeUrl ? "cursor-pointer" : "")}
          >
            <div className="relative sm:w-[271.33px] w-full h-[173px] overflow-hidden">
              <Image
                src={imageUrl}
                alt={postTitle}
                fill
                sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
                className="object-cover transition-opacity duration-300 group-hover:opacity-90"
                loading="lazy"
                quality={75}
              />
            </div>

            <div className="flex flex-col sm:gap-2 gap-1">
              <h3 className="sm:text-base sm:leading-[21.7px] text-sm font-bold leading-[21.7px] text-black group-hover:text-public-brand-strong transition-colors line-clamp-2">
                {postTitle}
              </h3>
              {publishedAt && <p className="text-xs text-black font-normal">{formatDateSimple(publishedAt)}</p>}
            </div>
          </Link>
        )
      })}
    </div>
  )
}

export default SubFeaturePosts
